import { easeOutSine } from "./sketch-easing";
import type { SketchPoint } from "./stroke-points";

/** Pen nib: full width in px and the start/end taper lengths in px. */
export type StrokeNib = { size: number; taper: readonly [number, number] };

export type OutlinePoint = { x: number; y: number };

type OutlineCommand =
  | { kind: "move"; to: OutlinePoint }
  | { kind: "quad"; control: OutlinePoint; to: OutlinePoint };

/** Variable-width stroke outline: each point is offset along its normal by
 * size/2 · pressure · (0.2 + 0.8 · taper), with the taper eased over the start
 * and end lengths (each capped at half the stroke). The left side is joined to
 * the reversed right side into one closed polygon. */
export function strokeOutline(
  points: readonly SketchPoint[],
  nib: StrokeNib,
): OutlinePoint[] {
  const count = points.length;
  if (count < 2) return [];
  const lengths = [0];
  for (let index = 1; index < count; index += 1) {
    const previous = points[index - 1]!;
    const point = points[index]!;
    lengths.push(
      lengths[index - 1]! +
        Math.hypot(point.x - previous.x, point.y - previous.y),
    );
  }
  const total = lengths[count - 1]!;
  const taperStart = Math.min(nib.taper[0], total / 2);
  const taperEnd = Math.min(nib.taper[1], total / 2);
  const left: OutlinePoint[] = [];
  const right: OutlinePoint[] = [];
  for (let index = 0; index < count; index += 1) {
    const point = points[index]!;
    const before = points[Math.max(0, index - 1)]!;
    const after = points[Math.min(count - 1, index + 1)]!;
    const magnitude = Math.hypot(after.x - before.x, after.y - before.y) || 1;
    const dx = (after.x - before.x) / magnitude;
    const dy = (after.y - before.y) / magnitude;
    const traveled = lengths[index]!;
    let taper = 1;
    if (taperStart > 0 && traveled < taperStart) {
      taper = Math.min(taper, easeOutSine(traveled / taperStart));
    }
    if (taperEnd > 0 && total - traveled < taperEnd) {
      taper = Math.min(taper, easeOutSine((total - traveled) / taperEnd));
    }
    const half = (nib.size / 2) * (point.p ?? 1) * (0.2 + 0.8 * taper);
    left.push({ x: point.x - dy * half, y: point.y + dx * half });
    right.push({ x: point.x + dy * half, y: point.y - dx * half });
  }
  return left.concat(right.reverse());
}

function midpoint(a: OutlinePoint, b: OutlinePoint): OutlinePoint {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/** Smooth closed curve through the polygon's edge midpoints, with each vertex
 * as a quadratic control point. Shared by the SVG and canvas emitters. */
function* outlineCurves(
  polygon: readonly OutlinePoint[],
): Generator<OutlineCommand> {
  if (polygon.length < 2) return;
  yield { kind: "move", to: midpoint(polygon[0]!, polygon[1]!) };
  for (let index = 1; index < polygon.length; index += 1) {
    const control = polygon[index]!;
    const next = polygon[(index + 1) % polygon.length]!;
    yield { kind: "quad", control, to: midpoint(control, next) };
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function svgPoint(point: OutlinePoint): string {
  return `${round2(point.x)} ${round2(point.y)}`;
}

/** SVG `d` for a filled outline: `M … Q … Z`, coordinates to 2 decimals. */
export function outlineSvgPath(polygon: readonly OutlinePoint[]): string {
  const parts: string[] = [];
  for (const command of outlineCurves(polygon)) {
    parts.push(
      command.kind === "move"
        ? `M${svgPoint(command.to)}`
        : `Q${svgPoint(command.control)} ${svgPoint(command.to)}`,
    );
  }
  return parts.length > 0 ? `${parts.join(" ")} Z` : "";
}

/** Traces the same outline onto a canvas path (e.g. a 2D context or Path2D). */
export function traceOutline(
  target: Pick<CanvasPath, "moveTo" | "quadraticCurveTo" | "closePath">,
  polygon: readonly OutlinePoint[],
): void {
  let traced = false;
  for (const command of outlineCurves(polygon)) {
    traced = true;
    if (command.kind === "move") {
      target.moveTo(command.to.x, command.to.y);
    } else {
      target.quadraticCurveTo(
        command.control.x,
        command.control.y,
        command.to.x,
        command.to.y,
      );
    }
  }
  if (traced) target.closePath();
}
